// Iter 306 — Domain transfers between agents: lookup, preview with
// consequences, confirm, history and revert. Replaces the Export / Import tab.
import { useEffect, useState } from "react";
import { AlertTriangle, ArrowRightLeft, Loader2, Search, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { money, apiError, thCls, tdCls } from "./engineShared";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export function EngineTransfers() {
  const [list, setList] = useState({ items: [], agents: [], can_manage: false });
  const [domain, setDomain] = useState("");
  const [lookup, setLookup] = useState(null);
  const [toAgent, setToAgent] = useState("");
  const [effective, setEffective] = useState(thisMonth());
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/commissions/engine/transfers").then(({ data }) => setList(data)).catch((e) => apiError(e, "Could not load transfers"));
  useEffect(() => { load(); }, []);

  const find = async () => {
    setBusy("lookup"); setPreview(null);
    try { const { data } = await api.get("/commissions/engine/transfers/lookup", { params: { domain } }); setLookup(data); if (!data.found) toast.error("No commission lines for that domain"); }
    catch (e) { apiError(e, "Lookup failed"); } finally { setBusy(""); }
  };
  const doPreview = async () => {
    setBusy("preview");
    try { const { data } = await api.post("/commissions/engine/transfers/preview", { domain: lookup.domain, to_agent_id: toAgent, effective_period: effective }); setPreview(data); }
    catch (e) { apiError(e, "Preview failed"); } finally { setBusy(""); }
  };
  const confirm = async () => {
    setBusy("apply");
    try {
      const { data } = await api.post("/commissions/engine/transfers", { domain: lookup.domain, to_agent_id: toAgent, effective_period: effective, reason, confirm: true });
      toast.success(`${data.transfer.domain} now belongs to ${data.transfer.to_agent} from ${effective} (${data.transfer.applied_months.length} month${data.transfer.applied_months.length === 1 ? "" : "s"} rebuilt)`);
      setPreview(null); setReason(""); setLookup(null); setDomain(""); load();
    } catch (e) { apiError(e, "Transfer failed"); } finally { setBusy(""); }
  };
  const revert = async (t) => {
    const why = window.prompt(`Revert the transfer of ${t.domain} back to ${t.from_agent || t.from_territory} (from ${t.effective_period})? Say why:`);
    if (why === null) return;
    try { await api.post(`/commissions/engine/transfers/${t.transfer_id}/revert`, { reason: why }); toast.success("Transfer reverted"); load(); }
    catch (e) { apiError(e, "Revert failed"); }
  };
  const agentOptions = list.agents.filter((a) => a.territory && (!lookup?.owner?.territory || a.territory.toLowerCase() !== lookup.owner.territory.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="engine-transfers-panel">
      <Card className="p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><ArrowRightLeft className="h-4 w-4 text-primary" /> Move a domain to another agent</div>
        <p className="mb-3 text-xs text-slate-500">
          Everything up to the month before the effective month stays with the current agent — payments, adjustments, rates and custom deal rates included.
          From the effective month on, the domain&apos;s statement lines belong to the new agent. Amounts are kept as computed; only the owner changes.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <Label className="text-xs">Domain</Label>
            <div className="flex gap-2">
              <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="xyz.com" onKeyDown={(e) => e.key === "Enter" && domain && find()} data-testid="xfer-domain" />
              <Button variant="outline" onClick={find} disabled={!domain || busy === "lookup"} data-testid="xfer-lookup">{busy === "lookup" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
            </div>
          </div>
        </div>
        {lookup?.found && (
          <div className="mt-4 space-y-3" data-testid="xfer-lookup-result">
            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-700">
              <b>{lookup.domain}</b>{lookup.customer_name && <> · {lookup.customer_name}</>} — currently <b>{lookup.owner.agent || lookup.owner.territory}</b> <span className="text-slate-500">({lookup.owner.territory}, latest month {lookup.owner.period_label})</span>
              <div className="mt-1 text-slate-500">History: {lookup.history.map((h) => `${h.period_label} ${h.agent || h.territory}`).filter((v, i, a) => a.indexOf(v) === i).slice(-6).join(" · ")}</div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="md:col-span-2">
                <Label className="text-xs">New agent</Label>
                <Select value={toAgent} onValueChange={setToAgent}>
                  <SelectTrigger data-testid="xfer-agent"><SelectValue placeholder="Pick the agent, reseller or service provider" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {agentOptions.map((a) => <SelectItem key={a.agent_id} value={a.agent_id}>{a.name} <span className="text-slate-400">· {a.territory}{a.agent_type ? ` · ${a.agent_type}` : ""}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Effective month (whole month moves)</Label>
                <Input type="month" value={effective} onChange={(e) => setEffective(e.target.value)} data-testid="xfer-effective" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer moved to reseller Otro on 10 Oct" data-testid="xfer-reason" />
            </div>
            <Button onClick={doPreview} disabled={!toAgent || !effective || busy === "preview" || !list.can_manage} data-testid="xfer-preview">
              {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />} Review consequences
            </Button>
            {!list.can_manage && <span className="ml-2 text-xs text-slate-400">Super admins only.</span>}
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-2 text-xs font-medium uppercase tracking-wider text-slate-500">Transfers</div>
        <table className="w-full text-sm" data-testid="xfer-table">
          <thead><tr><th className={thCls}>Domain</th><th className={thCls}>From</th><th className={thCls}>To</th><th className={thCls}>Effective</th><th className={thCls}>Impact</th><th className={thCls}>By</th><th className={thCls}></th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {list.items.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400" data-testid="xfer-empty">No transfers yet.</td></tr>}
            {list.items.map((t) => (
              <tr key={t.transfer_id} className={t.status === "reverted" ? "opacity-60" : ""} data-testid={`xfer-row-${t.transfer_id}`}>
                <td className={tdCls}><b>{t.domain}</b>{t.status === "reverted" && <span className="ml-1.5 rounded bg-slate-200 px-1 text-[10px] text-slate-600">reverted</span>}<div className="text-xs text-slate-500">{t.reason}</div></td>
                <td className={tdCls}>{t.from_agent || t.from_territory}</td>
                <td className={tdCls}>{t.to_agent}<div className="text-xs text-slate-400">{t.to_territory}</div></td>
                <td className={tdCls}>{t.effective_period}</td>
                <td className={tdCls}>{t.impact?.lines ?? 0} lines · {money(t.impact?.commission)}<div className="text-xs text-slate-400">{(t.impact?.periods || []).join(", ")}</div></td>
                <td className={tdCls}><div className="text-xs">{t.created_by_name}</div><div className="text-[10px] text-slate-400">{t.created_at?.slice(0, 10)}</div></td>
                <td className={tdCls}>{t.status === "active" && list.can_manage && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => revert(t)} data-testid={`xfer-revert-${t.transfer_id}`}><Undo2 className="h-3.5 w-3.5" /> Revert</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {preview && (
        <Dialog open onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="max-w-2xl" data-testid="xfer-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Transfer {preview.domain}?</DialogTitle>
              <DialogDescription>
                From <b>{preview.from.agent || preview.from.territory}</b> to <b>{preview.to.agent}</b> ({preview.to.territory}{preview.to.level_name ? `, ${preview.to.level_name}` : ""}) effective <b>{preview.effective_period}</b>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-slate-50 p-3 space-y-2" data-testid="xfer-impact">
                <div>
                  <b>{preview.lines}</b> statement line{preview.lines === 1 ? "" : "s"} worth <b>{money(preview.commission)}</b> commission move
                  {preview.periods.length ? <> across {preview.periods.map((p) => <span key={p.period_label} className="ml-1 rounded bg-white px-1.5 py-0.5 text-xs ring-1 ring-slate-200">{p.period_label}{p.accepted ? " · accepted" : ""}{p.paid ? ` · paid ${money(p.paid)}` : ""}</span>)}</> : " — no lines yet for those months; future syncs will attribute new invoices to the new agent."}
                </div>
                <ul className="space-y-1.5">
                  {preview.warnings.map((w, i) => <li key={i} className="flex gap-2 text-xs text-slate-700"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />{w}</li>)}
                  <li className="flex gap-2 text-xs text-slate-700"><Undo2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />You can revert this transfer later, or move the domain again if the right agent turns out to be someone else.</li>
                </ul>
              </div>
              <ConfirmTick onConfirm={confirm} busy={busy === "apply"} disabled={reason.trim().length < 3} onCancel={() => setPreview(null)} />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ConfirmTick({ onConfirm, onCancel, busy, disabled }) {
  const [ticked, setTicked] = useState(false);
  return (
    <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center">
      <label className="flex flex-1 items-center gap-2 text-xs text-slate-700">
        <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} data-testid="xfer-confirm-tick" /> I understand the consequences above
      </label>
      {disabled && <span className="text-xs text-rose-600">Add a reason first.</span>}
      <Button variant="outline" onClick={onCancel} data-testid="xfer-confirm-cancel">Cancel</Button>
      <Button onClick={onConfirm} disabled={!ticked || busy || disabled} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="xfer-confirm-apply">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />} Transfer domain
      </Button>
    </DialogFooter>
  );
}
